import { canOpenStaffPage, canStaffTab, staffPages, staffTabs, firstStaffTab, staffLanding } from '@/utils/staffAccess'
import { createRouter, createWebHistory } from 'vue-router'
import Login from '../views/Login.vue'
import PatientHome from '../views/PatientHome.vue'
import HealthTracking from '../views/HealthTracking.vue'
import BiometricRegister from '../views/BiometricRegister.vue'
import NurseCallsHistory from '../views/NurseCallsHistory.vue'
import PrivacyPolicy from '../views/PrivacyPolicy.vue'
import UserAgreement from '../views/UserAgreement.vue'
import LicenseAgreement from '../views/LicenseAgreement.vue'
import ServiceTerms from '../views/ServiceTerms.vue'
import ChangePassword from '../views/ChangePassword.vue'
import sessionBootstrap, {
  loadSessionBootstrap,
  resetSessionBootstrap
} from '../state/sessionBootstrap'
import { canAccessUltrasound } from '../utils/ultrasoundAccess'

const routes = [
  { path: '/staff/access', name: 'StaffAccessManagement',
    component: () => import('../views/StaffAccessManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true } },
  { path: '/staff/no-access', name: 'StaffNoAccess',
    component: () => import('../views/StaffNoAccess.vue'),
    meta: { requiresAuth: true, requiresStaff: true } },
  {
    path: '/',
    name: 'Home',
    component: () => import('../views/Home.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/patient-home',
    name: 'PatientHome',
    component: PatientHome,
    meta: { requiresAuth: true, requiresPatient: true }
  },
  {
    path: '/register',
    name: 'RegisterForm',
    redirect: '/biometric-register'
  },
  {
    path: '/biometric-register',
    name: 'BiometricRegister',
    component: BiometricRegister
  },
  {
    path: '/biometric-register-minor',
    name: 'BiometricRegisterMinor',
    redirect: '/biometric-finished'
  },
  {
    path: '/biometric-finished',
    name: 'BiometricFinished',
    component: BiometricRegister
  },
  {
    path: '/login',
    name: 'Login',
    component: Login
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('../views/ForgotPassword.vue')
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue')
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: ChangePassword,
    meta: { requiresAuth: true },
    props: route => ({
      isMandatory: route.query.mandatory === 'true',
      isPasswordExpired: route.query.expired === 'true'
    })
  },
  {
    path: '/doctor-schedule',
    name: 'DoctorSchedule',
    component: () => import('../views/DoctorSchedule.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/patients',
    name: 'Patients',
    component: () => import('../views/Patients.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/ultrasound',
    name: 'DoctorUltrasound',
    component: () => import('../views/DoctorUltrasound.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/ultrasound-standalone',
    name: 'UltrasoundStandalone',
    component: () => import('../views/UltrasoundStandalone.vue'),
    meta: { requiresAuth: true, requiresDoctor: true, standalone: true }
  },
  {
    path: '/api/ultrasound/embed/',
    name: 'UltrasoundEmbed',
    component: () => import('../views/UltrasoundStandalone.vue'),
    meta: { standalone: true, partnerEmbed: true }
  },
  {
    path: '/organization-patients',
    name: 'OrganizationPatients',
    component: () => import('../views/OrganizationPatients.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/correctional-institutions',
    name: 'DoctorCorrectionalInstitutions',
    component: () => import('../views/DoctorCorrectionalInstitutions.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/detected-faces',
    name: 'DetectedFaces',
    component: () => import('../views/DetectedFaces.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/appointments',
    name: 'Appointments',
    component: () => import('../views/Appointments.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/clinic-today',
    name: 'ClinicToday',
    component: () => import('../views/manager/ManagerToday.vue'),
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/clinic-today-24-7',
    name: 'ClinicToday247',
    component: () => import('../views/manager/ManagerToday.vue'),
    props: { scope: '24_7' },
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/clinic-appointments-24-7',
    name: 'ClinicAppointments247',
    component: () => import('../views/manager/ManagerRecords.vue'),
    props: { scope: '24_7' },
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/clinic-appointments',
    name: 'ClinicAppointments',
    component: () => import('../views/manager/ManagerRecords.vue'),
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/clinic-patients',
    name: 'ClinicPatients',
    component: () => import('../views/manager/ManagerPatients.vue'),
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/clinic-patients/:id(\\d+)',
    name: 'ClinicPatient',
    component: () => import('../views/manager/ManagerPatient.vue'),
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/clinic-checkups',
    name: 'ClinicCheckups',
    component: () => import('../views/manager/ManagerCheckups.vue'),
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/doctor-checkups',
    name: 'DoctorCheckups',
    component: () => import('../views/manager/ManagerCheckups.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/clinic-cash',
    name: 'ClinicCash',
    component: () => import('../views/manager/ManagerCash.vue'),
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/clinic-schedule',
    name: 'ClinicSchedule',
    component: () => import('../views/manager/ManagerSchedule.vue'),
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/clinic-finance',
    name: 'ClinicFinance',
    component: () => import('../views/manager/ManagerReports.vue'),
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/clinic-doctors',
    name: 'ClinicDoctors',
    component: () => import('../views/manager/ManagerDoctors.vue'),
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/clinic-settings',
    name: 'ClinicSettings',
    component: () => import('../views/ClinicSettings.vue'),
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    // Психомониторинг для тестового врача (использует DoctorLayout)
    path: '/psychological-monitoring',
    name: 'DoctorPsychologicalMonitoring',
    component: () => import('../views/staff/PsychologicalRiskDashboard.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/health-tracking',
    name: 'HealthTracking',
    component: HealthTracking,
    meta: { requiresAuth: true }
  },
  {
    path: '/nurse-calls-history',
    name: 'NurseCallsHistory',
    component: NurseCallsHistory,
    meta: { requiresAuth: true, requiresNurse: true }
  },
  {
    path: '/patients/:id',
    name: 'PatientDetails',
    component: () => import('../views/PatientDetails.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/patients/:patientId/tracker',
    name: 'PatientTracker',
    component: () => import('../views/PatientTracker.vue'),
    meta: { requiresAuth: true }
  },

  // Панель персонала (для is_staff)
  {
    path: '/staff',
    name: 'StaffDashboard',
    component: () => import('../views/StaffDashboard.vue'),
    meta: { requiresAuth: true, requiresStaff: true }
  },
  {
    path: '/correctional',
    name: 'CorrectionalDashboard',
    component: () => import('../views/CorrectionalDashboard.vue'),
    meta: { requiresAuth: true, requiresCorrectionalAdmin: true }
  },
  {
    path: '/correctional/patients',
    name: 'CorrectionalPatients',
    component: () => import('../views/CorrectionalPatients.vue'),
    meta: { requiresAuth: true, requiresCorrectionalAdmin: true }
  },
  {
    path: '/staff/locations',
    name: 'StaffLocations',
    component: () => import('../views/StaffLocations.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/cities',
    name: 'StaffCities',
    component: () => import('../views/StaffCities.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/medical-staff',
    name: 'StaffMedical',
    component: () => import('../views/StaffMedical.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/patients',
    name: 'StaffPatients',
    component: () => import('../views/StaffPatients.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/users',
    name: 'StaffUsersManagement',
    component: () => import('../views/StaffUsersManagement.vue'),
    meta: { requiresAuth: true, requiresStaff: true, chiefDoctorAccess: true }
  },
  {
    path: '/staff/personnel',
    name: 'StaffPersonnelManagement',
    redirect: '/staff/users?tab=personnel'
  },
  {
    path: '/staff/specializations',
    name: 'StaffSpecializations',
    component: () => import('../views/StaffSpecializations.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/organization-roles',
    name: 'StaffOrganizationRoles',
    component: () => import('../views/StaffOrganizationRoles.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/health-trackers',
    name: 'StaffHealthTrackers',
    component: () => import('../views/StaffHealthTrackers.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/visit-reasons',
    name: 'StaffVisitReasons',
    component: () => import('../views/StaffVisitReasons.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/cancellation-reasons',
    name: 'StaffCancellationReasons',
    component: () => import('../views/StaffCancellationReasons.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/logging',
    name: 'StaffLogging',
    component: () => import('../views/StaffLogging.vue'),
    meta: { requiresAuth: true, requiresStaff: true, chiefDoctorAccess: true }
  },
  {
    path: '/staff/org-reports',
    name: 'StaffOrgReports',
    component: () => import('../views/StaffOrgReports.vue'),
    meta: { requiresAuth: true, requiresStaff: true, chiefDoctorAccess: true }
  },
  {
    path: '/staff/indicators',
    name: 'StaffIndicators',
    redirect: to => ({
      path: '/staff/logging',
      query: { ...to.query, tab: 'indicators' }
    }),
    meta: { requiresAuth: true, requiresStaff: true, chiefDoctorAccess: true }
  },
  {
    path: '/staff/school-doctors',
    name: 'StaffSchoolDoctors',
    redirect: to => ({
      path: '/staff/logging',
      query: { ...to.query, tab: 'schoolDoctors' }
    }),
    meta: { requiresAuth: true, requiresStaff: true, chiefDoctorAccess: true }
  },
  {
    path: '/staff/support',
    name: 'StaffSupport',
    component: () => import('../views/staff/StaffSupport.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/support/:ticketId',
    name: 'StaffSupportChat',
    component: () => import('../views/staff/StaffSupportChat.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/surveys',
    name: 'StaffSurveys',
    component: () => import('../views/StaffSurveys.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/organizations',
    name: 'StaffOrganizations',
    component: () => import('../views/StaffOrganizations.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/surveys/create',
    name: 'SurveyBuilder',
    component: () => import('../views/staff/SurveyBuilder.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/surveys/:id/edit',
    name: 'SurveyEdit',
    component: () => import('../views/staff/SurveyBuilder.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/surveys/:id/statistics',
    name: 'SurveyStatistics',
    component: () => import('../views/staff/SurveyStatistics.vue'),
    meta: { requiresAuth: true, requiresStaff: true, chiefDoctorAccess: true }
  },
  {
    path: '/staff/psychological-monitoring',
    name: 'PsychologicalRiskDashboard',
    component: () => import('../views/staff/PsychologicalRiskDashboard.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  // Дэшборд для авторизованных пользователей
  {
    path: '/dashboard/almaty',
    name: 'DashboardPage',
    component: () => import('../views/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  // Документы - доступны всем
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy
  },

  {
    path: '/user-agreement',
    name: 'UserAgreement',
    component: UserAgreement
  },
  {
    path: '/license-agreement',
    name: 'LicenseAgreement',
    component: LicenseAgreement
  },
  {
    path: '/service-terms',
    name: 'ServiceTerms',
    component: ServiceTerms
  },
  {
    path: '/doctor-manual',
    name: 'DoctorManual',
    component: () => import('../views/DoctorManual.vue')
  },
  {
    path: '/user-manual',
    name: 'UserManual',
    component: () => import('../views/UserManual.vue')
  },
  // PDF прямого просмотра для мобильных приложений
  {
    path: '/pdf/:type/:lang',
    name: 'PdfViewer',
    component: () => import('../views/PdfViewer.vue'),
    props: true
  },
  {
    path: '/certificate',
    name: 'Certificate',
    component: () => import('../views/Certificate.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  // Техподдержка - доступна только врачам
  {
    path: '/support',
    name: 'SupportPage',
    component: () => import('../views/Support.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/support/:ticketId',
    name: 'SupportChat',
    component: () => import('../views/SupportChat.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Utility function to check if user has a specific role
const hasRole = (userTypes, role) => {
  return Array.isArray(userTypes) && userTypes.includes(role)
}

// Navigation guard
router.beforeEach(async (to, from, next) => {
  if (to.meta.partnerEmbed) { next(); return }

  // Skip authentication check for public pages
  if (to.path === '/login' || to.path === '/biometric-register' || to.path === '/biometric-register-minor' || to.path === '/biometric-finished' ||
    to.path === '/privacy-policy' || to.path === '/user-agreement' ||
    to.path === '/license-agreement' || to.path === '/service-terms' ||
    to.path === '/doctor-manual' || to.path === '/user-manual' ||
    to.path.startsWith('/pdf/')) {
    next()
    return
  }

  // If the route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    try {
      await loadSessionBootstrap()
      const user = sessionBootstrap.currentUser

      if (!user) {
        // Redirect to login page with return URL
        next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
        return
      }
      const userType = user?.type        // Основная роль для обратной совместимости
      const userTypes = user?.types || [] // Все роли пользователя

      // ПЕРВАЯ ПРОВЕРКА: Админские маршруты (только для is_superuser)
      if (to.matched.some(record => record.meta.requiresAdmin)) {
        if (!user.is_superuser) {
          // Редирект в зависимости от прав пользователя
          if (userType === 'staff') {
            next('/staff')  // Персонал идет на свою панель
          } else if (userType === 'doctor' || userType === 'nurse') {
            next('/')
          } else if (userType === 'patient') {
            next('/patient-home')
          } else {
            next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
          }
          return
        } else {
          next()
          return
        }
      }

      // Resolve staff navigation from the server policy, including direct URLs.
      if (user.is_staff || user.is_superuser) {
        const page = staffPages[to.name]
        if (page && user.staff_access) {
          if (!canOpenStaffPage(user, page)) {
            next(staffLanding(user))
          } else if (staffTabs[page] && !canStaffTab(user, page, to.query.tab || firstStaffTab(user, page))) {
            next({ path: to.path, query: { ...to.query, tab: firstStaffTab(user, page) }, replace: true })
          } else {
            next()
          }
          return
        }
        if (to.name === 'StaffNoAccess') { next(); return }
      }

      // ВТОРАЯ ПРОВЕРКА: Маршруты персонала (для is_staff, но не is_superuser)
      if (to.matched.some(record => record.meta.requiresStaff)) {
        // Проверяем, разрешён ли доступ тестовому врачу
        const allowTestDoctor = to.matched.some(record => record.meta.allowTestDoctor)
        const userName = user.name || ''
        const isTestDoctor = userName.includes('Сембинова') && userName.includes('Алтынай')

        if (!user.is_staff && !(allowTestDoctor && isTestDoctor)) {
          if (userType === 'doctor' || userType === 'nurse') {
            next('/')
          } else if (userType === 'patient') {
            next('/patient-home')
          } else {
            next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
          }
          return
        }

        // ДОПОЛНИТЕЛЬНАЯ ПРОВЕРКА: Ограничения для ролей с ограниченным доступом
        const userStaffRole = user.staff_role || 'staff'
        if (userStaffRole === 'correctional_admin') {
          next('/correctional')
          return
        }
        if (userStaffRole === 'clinic_manager') {
          next('/clinic-today')
          return
        }
        if (userStaffRole === 'suicide_prevention_observer') {
          if (to.name !== 'StaffLogging' && to.name !== 'StaffIndicators') {
            next('/staff/logging')
          } else {
            next()
          }
          return
        }
        const isOrgDirector = ['org_director', 'observer'].includes(userStaffRole)
        const isChiefDoctor = ['chief_doctor', 'org_director', 'observer'].includes(userStaffRole)
        const isClinicAdmin = userStaffRole === 'clinic_admin'
        const hasLimitedAccess = isChiefDoctor || isClinicAdmin

        if (isClinicAdmin && to.name === 'StaffLogging') {
          next('/staff/users')
          return
        }

        if (isClinicAdmin && to.name === 'PsychologicalRiskDashboard') {
          next('/staff/users')
          return
        }

        if (to.name === 'StaffOrgReports' && !isOrgDirector) {
          next(isClinicAdmin ? '/staff/users' : '/staff/logging')
          return
        }

        // Для директора организации и обозревателя закрываем доступ к старой странице отчетов и статистике опросов
        if (isOrgDirector && to.name === 'StaffLogging') {
          next('/staff/org-reports')
          return
        }
        if (isOrgDirector && to.name === 'SurveyStatistics') {
          next('/staff/org-reports')
          return
        }

        // Страницы только для обычного персонала (недоступны для Главного врача и Администратора учреждения)
        if (to.matched.some(record => record.meta.staffOnly) && hasLimitedAccess) {
          next('/staff/users')  // Редирект на страницу пользователей
          return
        }

        // Страницы, доступные для персонала, Главного врача и Администратора учреждения
        if (to.matched.some(record => record.meta.chiefDoctorAccess) ||
          !to.matched.some(record => record.meta.staffOnly)) {
          next()
          return
        }

        // Обычный персонал имеет доступ ко всем страницам
        if (!hasLimitedAccess) {
          next()
          return
        }
      }

      if (to.matched.some(record => record.meta.requiresCorrectionalAdmin)) {
        const isCorrectionalAdmin = user.is_staff && user.staff_role === 'correctional_admin'
        if (!isCorrectionalAdmin) {
          next(user.is_staff ? '/staff' : '/')
          return
        }
      }

      // Redirect to appropriate home page based on user type
      if (to.path === '/') {
        if (userType === 'staff') {
          if (user.staff_role === 'clinic_manager') {
            next('/clinic-today')
          } else if (user.staff_role === 'correctional_admin') {
            next('/correctional')
          } else if (user.staff_role === 'suicide_prevention_observer') {
            next('/staff/logging')
          } else {
            next('/staff')
          }
          return
        } else if (userType === 'doctor' || userType === 'nurse') {
          // For doctors and nurses, allow access to home page
          // Note: Schedule check temporarily disabled to allow normal navigation
          next()
          return
        } else if (userType === 'patient') {
          next('/patient-home')
          return
        } else {
          // Пользователь без типа - проверяем права
          if (user.is_superuser) {
            next('/staff')
            return
          } else if (user.is_staff) {
            next('/staff')
            return
          } else {
            next('/login')
            return
          }
        }
      }

      // If the route requires clinic manager role
      if (to.matched.some(record => record.meta.requiresClinicManager)) {
        const isClinicManager = user.is_staff && user.staff_role === 'clinic_manager'

        if (!isClinicManager) {
          if (user.is_staff || hasRole(userTypes, 'staff')) {
            next('/staff')
          } else if (hasRole(userTypes, 'doctor') || hasRole(userTypes, 'nurse')) {
            next('/')
          } else if (hasRole(userTypes, 'patient')) {
            next('/patient-home')
          } else {
            next('/login')
          }
          return
        }
      }

      // If the route requires doctor role (allow both doctor and nurse)
      if (to.matched.some(record => record.meta.requiresDoctor)) {
        if (!hasRole(userTypes, 'doctor') && !hasRole(userTypes, 'nurse')) {
          // Not a doctor or nurse, redirect to appropriate home
          if (user.is_staff || hasRole(userTypes, 'staff')) {
            next('/staff')
          } else if (hasRole(userTypes, 'patient')) {
            next('/patient-home')
          } else {
            next('/login')
          }
          return
        }

        if (['DoctorUltrasound', 'UltrasoundStandalone'].includes(to.name)) {
          if (!hasRole(userTypes, 'doctor') || !canAccessUltrasound(sessionBootstrap.profile)) {
            const hasOrgs = Boolean(
              sessionBootstrap.profile?.has_organization_memberships ||
              (sessionBootstrap.profile?.organization_memberships && sessionBootstrap.profile.organization_memberships.length > 0)
            )
            next(hasOrgs ? '/organization-patients' : '/')
            return
          }
        }
      }

      // If the route requires nurse role
      if (to.matched.some(record => record.meta.requiresNurse)) {
        if (!hasRole(userTypes, 'nurse')) {
          // Not a nurse, redirect to appropriate home
          if (hasRole(userTypes, 'doctor')) {
            next('/')
          } else if (user.is_staff || hasRole(userTypes, 'staff')) {
            next('/staff')
          } else if (hasRole(userTypes, 'patient')) {
            next('/patient-home')
          } else {
            next('/login')
          }
          return
        }
      }

      // If the route requires patient role
      if (to.matched.some(record => record.meta.requiresPatient)) {
        if (!hasRole(userTypes, 'patient')) {
          // Not a patient, redirect to appropriate home
          if (user.is_staff || hasRole(userTypes, 'staff')) {
            next('/staff')
          } else if (hasRole(userTypes, 'doctor') || hasRole(userTypes, 'nurse')) {
            next('/')
          } else {
            next('/login')
          }
          return
        }
      }

      next()
    } catch (error) {
      next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
    }
  } else {
    next()
  }
})

export default router
export function clearUserCache() {
  resetSessionBootstrap()
}
